import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { 
  FaShoppingCart, FaUser, FaCalendar, FaBox, FaTag, 
  FaFileInvoice, FaShoppingBag, FaCreditCard, FaSort,
  FaSortUp, FaSortDown, FaEye, FaSearch, FaFilter,
  FaExclamationCircle, FaCheckCircle, FaCircle, FaTruck,
  FaStoreAlt, FaShieldAlt, FaExclamationTriangle, FaGlobe
} from 'react-icons/fa';
import './OrdersList.css';
import './UI/SearchInput.css';
import { useTranslation } from 'react-i18next';

// Dummy data for "experience" role
const dummyExperienceOrders = [
  { 
    id: 'exp_ord_1', order_number: 'EXP1001', created_at: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000).toISOString(), 
    customer: { name: 'John Experience', email: 'john.exp@example.com' },
    line_items: [{ title: 'Galaxy T-Shirt', quantity: 1, price: '29.99' }, { title: 'Astro Mug', quantity: 1, price: '15.99' }],
    total_price: '45.98', financial_status: 'paid', fulfillment_status: 'fulfilled', 
    source_marketplace: 'Unanimity Marketplace',
    tracking: [{ tracking_company: 'UPS', tracking_number: '1Z999AA10123456789', tracking_url: '#', status: 'delivered', tracking_details: { status_description: 'Delivered successfully', estimated_delivery: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000).toISOString() } }]
  },
  { 
    id: 'exp_ord_2', order_number: 'EXP1002', created_at: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(), 
    customer: { name: 'Jane Demo', email: 'jane.demo@example.com' },
    line_items: [{ title: 'Nebula Hoodie', quantity: 1, price: '49.99' }],
    total_price: '49.99', financial_status: 'pending', fulfillment_status: 'unfulfilled', 
    source_marketplace: 'Amazon DE'
  },
  { 
    id: 'exp_ord_3', order_number: 'EXP1003', created_at: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString(), 
    customer: { name: 'Test User Alpha', email: 'alpha.test@example.com' },
    line_items: [{ title: 'Rocket Cap', quantity: 2, price: '19.99' }],
    total_price: '39.98', financial_status: 'paid', fulfillment_status: 'partially_fulfilled', 
    source_marketplace: 'Shopify Store',
    tracking: [{ tracking_company: 'FedEx', tracking_number: '9876543210000', tracking_url: '#', status: 'in_transit', tracking_details: { status_description: 'In transit to destination city', estimated_delivery: new Date(Date.now() + 2 * 24 * 60 * 60 * 1000).toISOString() } }]
  },
  { 
    id: 'exp_ord_4', order_number: 'EXP1004', created_at: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString(), 
    customer: { name: 'Sample Customer', email: 'sample.cust@example.com' },
    line_items: [{ title: 'Steel Water Bottle', quantity: 1, price: '27.99' }, { title: 'Alien Socks', quantity: 3, price: '12.99' }],
    total_price: '66.96', financial_status: 'refunded', fulfillment_status: 'fulfilled', 
    source_marketplace: 'eBay'
  },
  { 
    id: 'exp_ord_5', order_number: 'EXP1005', created_at: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(), 
    customer: { name: 'Experience Shopper', email: 'shopper.exp@example.com' },
    line_items: [{ title: 'Voyager Backpack', quantity: 1, price: '59.99' }],
    total_price: '59.99', financial_status: 'paid', fulfillment_status: 'unfulfilled', 
    source_marketplace: 'TikTok Shop'
  },
   { 
    id: 'exp_ord_6', order_number: 'EXP1006', created_at: new Date(Date.now() - 10 * 24 * 60 * 60 * 1000).toISOString(), 
    customer: { name: 'Old Order Guy', email: 'old.order@example.com' },
    line_items: [{ title: 'Galaxy T-Shirt', quantity: 1, price: '29.99' }],
    total_price: '29.99', financial_status: 'paid', fulfillment_status: 'fulfilled', 
    source_marketplace: 'Unanimity Marketplace',
    tracking: [{ tracking_company: 'DHL', tracking_number: 'DHL999AA101', tracking_url: '#', status: 'delivered' , tracking_details: { status_description: 'Delivered to local post office', estimated_delivery: new Date(Date.now() - 9 * 24 * 60 * 60 * 1000).toISOString() }}]
  }
];

const OrdersList = () => {
  const [orders, setOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [pagination, setPagination] = useState({
    current_page: 1,
    total_pages: 1,
    total_count: 0,
    limit: 20,
    has_next: false,
    has_prev: false
  });
  const [sortConfig, setSortConfig] = useState({
    key: 'created_at',
    direction: 'desc'
  });
  
  const navigate = useNavigate();
  const { t } = useTranslation();

  // Transform order data from new API format to match component expectations
  const transformOrderData = (apiOrder) => {
    // Log the raw API order data
    console.log('🔍 Raw API Order Data:', JSON.stringify(apiOrder, null, 2));
    console.log('🎯 Risk Score in API Order:', apiOrder.risk_prediction?.risk_score);
    
    const transformedOrder = {
      id: apiOrder._id,
      order_number: apiOrder.order_number,
      created_at: apiOrder.created_at,
      customer: {
        // Fix: Use billing address name instead of order name
        name: apiOrder.billing_address?.name || apiOrder.shipping_address?.name || null,
        email: apiOrder.customer_email || null
      },
      line_items: apiOrder.line_items || [],
      total_price: apiOrder.order_total?.toString() || '0.00',
      financial_status: apiOrder.financial_status,
      fulfillment_status: apiOrder.fulfillment_status,
      // Replace source_marketplace with shipping_country
      shipping_country: apiOrder.shipping_address?.country || apiOrder.billing_address?.country || null,
      tracking: apiOrder.tracking || [],
      risk_prediction_score: apiOrder.risk_prediction?.risk_score || null,
      // Include any other fields that might be needed
      ...apiOrder
    };
    
    // Log the transformed order data
    console.log('✅ Transformed Order Data:', JSON.stringify(transformedOrder, null, 2));
    console.log('🎯 Risk Score in Transformed Order:', transformedOrder.risk_prediction_score);
    
    return transformedOrder;
  };

  useEffect(() => {
    const fetchOrders = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const token = localStorage.getItem('access_token');
        const userDataString = localStorage.getItem('userData');
        let userRole = null;

        if (!token) {
          navigate('/');
          setIsLoading(false);
          return;
        }

        if (userDataString) {
          try {
            const userData = JSON.parse(userDataString);
            userRole = userData.role;
          } catch (parseError) {
            console.error("Failed to parse user data from localStorage:", parseError);
          }
        }

        // Handle experience user with dummy data
        if (userRole === 'experience') {
          console.log("Experience user detected, loading dummy orders.");
          setOrders(dummyExperienceOrders);
          setFilteredOrders(dummyExperienceOrders);
          setIsLoading(false);
          return;
        }

        // Prepare API parameters
        const params = {
          page: pagination.current_page,
          limit: pagination.limit,
          sort_by: sortConfig.key,
          sort_order: sortConfig.direction
        };

        // Add search parameter if provided
        if (searchTerm) {
          params.search = searchTerm;
        }

        // Add status filters if provided
        if (statusFilter !== 'all') {
          if (['paid', 'pending', 'refunded'].includes(statusFilter)) {
            params.financial_status = statusFilter;
          } else if (['fulfilled', 'unfulfilled', 'partially_fulfilled'].includes(statusFilter)) {
            params.fulfillment_status = statusFilter;
          }
        }

        console.log("Fetching orders from orders_management endpoint with params:", params);

        const response = await axios.get('/api/orders_management/orders', {
          headers: { Authorization: `Bearer ${token}` },
          params: params
        });

        console.log('📥 Full API Response:', JSON.stringify(response.data, null, 2));

        if (response.data && response.data.orders) {
          console.log('📦 Raw Orders from API:', JSON.stringify(response.data.orders, null, 2));
          
          // Log individual order risk scores before transformation
          response.data.orders.forEach((order, index) => {
            console.log(`🔍 Order ${index + 1} (${order.order_number || order._id}):`, {
              risk_prediction_score: order.risk_prediction_score,
              hasRiskScore: order.risk_prediction_score !== undefined && order.risk_prediction_score !== null
            });
          });
          
          // Transform the orders to match component expectations
          const transformedOrders = response.data.orders.map(transformOrderData);
          
          console.log('🎯 Final Transformed Orders:', JSON.stringify(transformedOrders, null, 2));
          
          // Log risk scores in final transformed orders
          transformedOrders.forEach((order, index) => {
            console.log(`✅ Final Order ${index + 1} (${order.order_number}) Risk Score:`, {
              risk_prediction_score: order.risk_prediction_score,
              type: typeof order.risk_prediction_score,
              isNull: order.risk_prediction_score === null,
              isUndefined: order.risk_prediction_score === undefined,
              formatted: order.risk_prediction_score !== null && order.risk_prediction_score !== undefined 
                ? (order.risk_prediction_score * 100).toFixed(0) + '%' 
                : '-'
            });
          });
          
          setOrders(transformedOrders);
          setFilteredOrders(transformedOrders);
          
          // Update pagination info
          if (response.data.pagination) {
            setPagination(response.data.pagination);
          }
        } else {
          console.warn("Orders data not found in expected format:", response.data);
          setOrders([]);
          setFilteredOrders([]);
        }

      } catch (err) {
        console.error("Error fetching orders:", err);
        setError(err.response?.data?.detail || err.message || t('orders.fetchError'));
        setOrders([]);
        setFilteredOrders([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchOrders();
  }, [pagination.current_page, pagination.limit, searchTerm, statusFilter, sortConfig, navigate, t]);

  // Since filtering and sorting are now handled by the API, we remove the client-side filtering useEffect
  // The filteredOrders will be the same as orders since the API handles filtering

  const handleOrderClick = (orderId) => {
    navigate(`/order/${orderId}`);
  };

  const getStatusIcon = (status) => {
    if (!status) return <FaCircle className="status-icon status-neutral" />;
    
    switch (status.toLowerCase()) {
      case 'paid':
      case 'fulfilled':
        return <FaCheckCircle className="status-icon status-success" />;
      case 'pending':
      case 'partially_fulfilled':
        return <FaCircle className="status-icon status-pending" />;
      case 'unfulfilled':
      case 'refunded':
      case 'voided':
        return <FaExclamationCircle className="status-icon status-danger" />;
      default:
        return <FaCircle className="status-icon status-neutral" />;
    }
  };

  const getTrackingStatusIcon = (tracking) => {
    if (!tracking || tracking.length === 0) return null;
    
    return <FaTruck className="status-icon tracking-icon" />;
  };

  const getStatusClass = (status) => {
    if (!status) return 'una-status-neutral';
    
    switch (status.toLowerCase()) {
      case 'paid':
      case 'fulfilled':
        return 'una-status-success';
      case 'pending':
      case 'partially_fulfilled':
        return 'una-status-pending';
      case 'unfulfilled':
      case 'refunded':
      case 'voided':
        return 'una-status-danger';
      default:
        return 'una-status-neutral';
    }
  };

  const requestSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const getSortIcon = (columnName) => {
    if (sortConfig.key !== columnName) return <FaSort className="sort-icon" />;
    
    return sortConfig.direction === 'asc' 
      ? <FaSortUp className="sort-icon active" />
      : <FaSortDown className="sort-icon active" />;
  };

  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'short', day: 'numeric' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  const getItemsCount = (lineItems) => {
    if (!lineItems || !Array.isArray(lineItems)) return 0;
    return lineItems.reduce((sum, item) => sum + (item.quantity || 0), 0);
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    // Reset to first page when searching
    setPagination(prev => ({ ...prev, current_page: 1 }));
  };

  const handleStatusFilterChange = (e) => {
    setStatusFilter(e.target.value);
    // Reset to first page when filtering
    setPagination(prev => ({ ...prev, current_page: 1 }));
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, current_page: newPage }));
  };

  const getRiskScoreIcon = (score) => {
    console.log('🎯 getRiskScoreIcon called with:', { score, type: typeof score });
    
    if (score === null || score === undefined) return <FaCircle className="risk-icon risk-unknown" />;
    
    if (score >= 0.8) return <FaExclamationTriangle className="risk-icon risk-high" />;
    if (score >= 0.6) return <FaExclamationCircle className="risk-icon risk-medium" />;
    if (score >= 0.4) return <FaShieldAlt className="risk-icon risk-low" />;
    return <FaCheckCircle className="risk-icon risk-safe" />;
  };

  const getRiskScoreClass = (score) => {
    console.log('🎯 getRiskScoreClass called with:', { score, type: typeof score });
    
    if (score === null || score === undefined) return 'una-risk-unknown';
    
    if (score >= 0.8) return 'una-risk-high';
    if (score >= 0.6) return 'una-risk-medium';
    if (score >= 0.4) return 'una-risk-low';
    return 'una-risk-safe';
  };

  const formatRiskScore = (score) => {
    console.log('🎯 formatRiskScore called with:', { score, type: typeof score });
    
    if (score === null || score === undefined) return '-';
    return (score * 100).toFixed(0) + '%';
  };

  if (error) return (
    <div className="una-order-error">
      <FaExclamationCircle />
      <h2>{t('orders.error')}</h2>
      <p>{error}</p>
    </div>
  );
  
  if (isLoading) {
    return (
      <div className="una-dashboard-loading-screen">
        <div className="una-loading-content">
          <div className="una-loading-logo">
            <img src="/bgless_logo.png" alt="Unanimity AI Logo" />
          </div>
          <h2>{t('ordersP.loadingTitle')}</h2>
          <div className="una-loading-progress">
            <div className="una-progress-bar">
              <div className="una-progress-glow"></div>
            </div>
          </div>
          <div className="una-loading-metrics">
            <div className="una-metric-item">
              <FaShoppingCart />
              <span>{t('ordersP.fetchingOrders')}</span>
            </div>
            <div className="una-metric-item">
              <FaUser />
              <span>{t('ordersP.loadingCustomerData')}</span>
            </div>
            <div className="una-metric-item">
              <FaShoppingBag />
              <span>{t('ordersP.processingItems')}</span>
            </div>
            <div className="una-metric-item">
              <FaCreditCard />
              <span>{t('ordersP.verifyingPayments')}</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="una-orders-container">
      <div className="una-orders-header">
        <div className="una-orders-title">
          <FaShoppingCart />
          <h2>{t('ordersP.title')}</h2>
          <span className="una-orders-count">
            {pagination.total_count} {t('ordersP.totalOrders')}
          </span>
        </div>
        
        <div className="una-orders-actions">
          <div className="una-search-wrapper">
            <FaSearch className="una-search-icon" />
            <input
              type="text"
              className="una-search-input"
              placeholder={t('ordersP.searchPlaceholder')}
              value={searchTerm}
              onChange={handleSearchChange}
            />
            {searchTerm && (
              <button 
                className="una-clear-search" 
                onClick={() => setSearchTerm('')}
              >
                ×
              </button>
            )}
          </div>
          
          <div className="una-filter-wrapper">
            <FaFilter className="una-filter-icon" />
            <select 
              className="una-filter-select"
              value={statusFilter}
              onChange={handleStatusFilterChange}
            >
              <option value="all">{t('ordersP.allStatuses')}</option>
              <option value="paid">{t('ordersP.paid')}</option>
              <option value="pending">{t('ordersP.pending')}</option>
              <option value="refunded">{t('ordersP.refunded')}</option>
              <option value="fulfilled">{t('ordersP.fulfilled')}</option>
              <option value="unfulfilled">{t('ordersP.unfulfilled')}</option>
              <option value="partially_fulfilled">{t('ordersP.partiallyFulfilled')}</option>
            </select>
          </div>
        </div>
      </div>

      {!filteredOrders.length ? (
        <div className="una-orders-empty-state">
          <FaShoppingCart className="empty-icon" />
          <p>
            {searchTerm || statusFilter !== 'all'
              ? t('ordersP.noOrdersFiltered')
              : t('ordersP.noOrders')
            }
          </p>
          {(searchTerm || statusFilter !== 'all') && (
            <button 
              className="una-clear-filters-button"
              onClick={() => {
                setSearchTerm('');
                setStatusFilter('all');
              }}
            >
              {t('ordersP.clearFilters')}
            </button>
          )}
        </div>
      ) : (
        <div className="una-orders-table-container">
          <table className="una-orders-table">
            <thead>
              <tr>
                <th onClick={() => requestSort('order_number')} className="sortable">
                  {t('ordersP.orderNumber')}
                  {getSortIcon('order_number')}
                </th>
                <th onClick={() => requestSort('created_at')} className="sortable">
                  {t('ordersP.date')}
                  {getSortIcon('created_at')}
                </th>
                <th onClick={() => requestSort('order_name')} className="sortable">
                  {t('ordersP.customer')}
                  {getSortIcon('order_name')}
                </th>
                <th onClick={() => requestSort('shipping_country')} className="sortable">
                  {t('ordersP.country', 'Country')}
                  {getSortIcon('shipping_country')}
                </th>
                <th onClick={() => requestSort('risk_prediction_score')} className="sortable risk-column">
                  Risk Score
                  {getSortIcon('risk_prediction_score')}
                </th>
                <th className="status-column">
                  {t('ordersP.status')}
                </th>
                <th className="sortable items-column">
                  {t('ordersP.items')}
                </th>
                <th onClick={() => requestSort('order_total')} className="sortable price-column">
                  {t('ordersP.total')}
                  {getSortIcon('order_total')}
                </th>
              </tr>
            </thead>
            <tbody>
              {filteredOrders.map((order) => (
                <tr 
                  key={order.id} 
                  className="una-order-row" 
                  onClick={() => handleOrderClick(order.id)}
                >
                  <td className="order-number">
                    #{order.order_number}
                  </td>
                  <td>
                    <div className="una-cell-with-icon">
                      <FaCalendar className="cell-icon" />
                      <span>{formatDate(order.created_at)}</span>
                    </div>
                  </td>
                  <td>
                    <div className="una-cell-with-icon">
                      <FaUser className="cell-icon" />
                      <span>{order.customer?.name || order.customer?.email || t('ordersP.guestCustomer')}</span>
                    </div>
                  </td>
                  <td className="country-cell">
                    {order.shipping_country ? (
                      <div className="una-cell-with-icon">
                        <FaGlobe className="cell-icon" />
                        <span>{order.shipping_country}</span>
                      </div>
                    ) : (
                      '-' 
                    )}
                  </td>
                  <td className="risk-cell">
                    <div className={`una-risk-score ${getRiskScoreClass(order.risk_prediction_score)}`}>
                      {getRiskScoreIcon(order.risk_prediction_score)}
                      <span>{formatRiskScore(order.risk_prediction_score)}</span>
                    </div>
                  </td>
                  <td className="status-cell">
                    <div className="una-status-group">
                      <div className={`una-status-pill ${getStatusClass(order.financial_status)}`}>
                        {getStatusIcon(order.financial_status)}
                        <span>{order.financial_status || t('ordersP.unknown')}</span>
                      </div>
                      {order.fulfillment_status && (
                        <div className={`una-status-pill ${getStatusClass(order.fulfillment_status)}`}>
                          {getStatusIcon(order.fulfillment_status)}
                          <span>{order.fulfillment_status}</span>
                        </div>
                      )}
                      {order.tracking && order.tracking.length > 0 && (
                        <div className="una-status-pill una-status-tracking">
                          <FaTruck className="status-icon" />
                          <span>{t('ordersP.tracked')}</span>
                          <div className="una-tracking-tooltip">
                            <div className="una-tooltip-header">{t('ordersP.trackingInfo')}</div>
                            <ul className="una-tracking-items">
                              {order.tracking.map((track, idx) => (
                                <li key={idx}>
                                  <div className="una-tracking-item-header">
                                    <span>{track.tracking_company || t('ordersP.unknownCarrier')}</span>
                                    <span className={`una-tracking-status ${track.status?.toLowerCase()}`}>
                                      {track.status}
                                    </span>
                                  </div>
                                  <div className="una-tracking-number">
                                    {track.tracking_number}
                                    {track.tracking_url && (
                                      <a 
                                        href={track.tracking_url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        onClick={(e) => e.stopPropagation()}
                                        className="una-tracking-link"
                                      >
                                        {t('ordersP.track')}
                                      </a>
                                    )}
                                  </div>
                                  {track.tracking_details && (
                                    <div className="una-tracking-details">
                                      <div className="una-tracking-status-label">
                                        {track.tracking_details.status_description || track.tracking_details.status || t('ordersP.inTransit')}
                                      </div>
                                      {track.tracking_details.estimated_delivery && (
                                        <div className="una-delivery-estimate">
                                          {t('ordersP.estimatedDelivery')}: {formatDate(track.tracking_details.estimated_delivery)}
                                        </div>
                                      )}
                                    </div>
                                  )}
                                </li>
                              ))}
                            </ul>
                          </div>
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="items-cell">
                    <div className="una-cell-with-icon">
                      <FaBox className="cell-icon" />
                      <span>{getItemsCount(order.line_items)}</span>
                    </div>
                    <div className="una-cell-tooltip">
                      <ul className="una-tooltip-items">
                        {order.line_items.map((item, index) => (
                          <li key={index}>
                            <span>{item.title}</span>
                            <div>
                              <span>x{item.quantity}</span>
                              <span>${item.price}</span>
                            </div>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </td>
                  <td className="price-cell">${parseFloat(order.total_price).toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      
      <div className="una-orders-footer">
        <div className="una-pagination-info">
          {t('ordersP.showing')} {filteredOrders.length} {t('ordersP.of')} {pagination.total_count} {t('ordersP.orders')}
        </div>
        
        {pagination.total_pages > 1 && (
          <div className="una-pagination-controls">
            <button 
              className="una-pagination-button" 
              onClick={() => handlePageChange(pagination.current_page - 1)}
              disabled={!pagination.has_prev}
            >
              {t('ordersP.previous')}
            </button>
            <span className="una-pagination-info">
              {t('ordersP.page')} {pagination.current_page} {t('ordersP.of')} {pagination.total_pages}
            </span>
            <button 
              className="una-pagination-button" 
              onClick={() => handlePageChange(pagination.current_page + 1)}
              disabled={!pagination.has_next}
            >
              {t('ordersP.next')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default OrdersList;